import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { changesApi } from '../../services/changesApi';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  formatSecurityAuditActor,
  labelSecurityAuditEvent,
  SECURITY_AUDIT_EVENT_FILTER_OPTIONS,
  SECURITY_AUDIT_UNAUDITED_LABELS,
} from './changeLabels';
import { canViewChangeSecurityAudit } from './changePermissions';

type AuditRow = {
  id: number;
  actor_user_id: number | null;
  actor_email: string | null;
  actor_name?: string | null;
  actor_label?: string | null;
  event_type: string;
  description: string;
  change_request_id: number | null;
  client_ip: string | null;
  created_at: string;
};

const ChangeSecurityAuditPage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [eventType, setEventType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [changeId, setChangeId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await changesApi.listSecurityAudit({
        skip: 0,
        limit: 100,
        email: email.trim() || undefined,
        event_type: eventType.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        change_request_id: changeId ? Number(changeId) : undefined,
      });
      setItems(data.items as AuditRow[]);
      setTotal(data.total);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar a auditoria.'));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [changeId, dateFrom, dateTo, email, eventType]);

  useEffect(() => {
    if (!canViewChangeSecurityAudit(user)) return;
    void load();
  }, [load, user]);

  if (!canViewChangeSecurityAudit(user)) {
    return <Navigate to="/changes" replace />;
  }

  return (
    <div>
      <h5 className="mb-3">Auditoria de mudanças</h5>
      <p className="small text-muted mb-1">
        Rastreabilidade do módulo. Cada linha responde: quem fez, o que aconteceu e em qual
        mudança. O e-mail de gatilho registra o enfileiramento (não a confirmação SMTP).
      </p>
      <p className="small text-muted mb-1">
        Entra nesta tela: criação/edição/status, aprovações e reprovações, comentários,
        evidências (anexos), subtarefas e chamados Incidente, gatilhos de e-mail, pós-implantação,
        formulários de criação/pós, abas da ficha, apresentação CAB, convocação de reunião,
        rollback e permissões do módulo.
      </p>
      <p className="small text-muted">
        Ainda não entra nesta tela: {SECURITY_AUDIT_UNAUDITED_LABELS.join('; ')}.
      </p>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      <Form
        className="row g-2 mb-3 align-items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <div className="col-md-2">
          <Form.Label className="small mb-0">De</Form.Label>
          <Form.Control
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <Form.Label className="small mb-0">Até</Form.Label>
          <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="col-md-3">
          <Form.Label className="small mb-0">E-mail</Form.Label>
          <Form.Control
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="trecho do e-mail"
          />
        </div>
        <div className="col-md-2">
          <Form.Label className="small mb-0">Evento</Form.Label>
          <Form.Select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            aria-label="Filtrar por tipo de evento"
          >
            <option value="">Todos</option>
            {SECURITY_AUDIT_EVENT_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-1">
          <Form.Label className="small mb-0">Mudança</Form.Label>
          <Form.Control
            value={changeId}
            onChange={(e) => setChangeId(e.target.value)}
            placeholder="ID"
          />
        </div>
        <div className="col-md-2">
          <Button type="submit" size="sm" disabled={loading}>
            {loading ? 'Carregando…' : 'Filtrar'}
          </Button>
        </div>
      </Form>
      <div className="small text-muted mb-2">{total} registro(s)</div>
      <Table responsive size="sm" bordered hover>
        <thead>
          <tr>
            <th>Quando</th>
            <th>Usuário</th>
            <th>Evento</th>
            <th>Descrição</th>
            <th>Mudança</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-muted">
                Nenhum registro.
              </td>
            </tr>
          ) : (
            items.map((row) => (
              <tr key={row.id}>
                <td className="small text-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleString('pt-BR') : '—'}
                </td>
                <td className="small">{formatSecurityAuditActor(row)}</td>
                <td className="small">{labelSecurityAuditEvent(row.event_type)}</td>
                <td className="small">{row.description}</td>
                <td className="small">{row.change_request_id ?? '—'}</td>
                <td className="small">{row.client_ip || '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ChangeSecurityAuditPage;
